import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon, SparklesIcon, HandThumbUpIcon, HandThumbDownIcon, LifebuoyIcon, ArrowPathIcon, BookOpenIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as ThumbUpSolid, HandThumbDownIcon as ThumbDownSolid } from '@heroicons/react/24/solid';
import { useHelpCentre } from '../../contexts/HelpCentreContext';
import HelpMarkdown from './HelpMarkdown';

function EscalateBox({ questionId, onDone }) {
  const { t } = useTranslation();
  const { escalate } = useHelpCentre();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const send = async () => {
    setBusy(true);
    setErr('');
    try {
      await escalate(questionId, note);
      onDone();
    } catch (e) {
      setErr(e.message || t('admin:help.chat.escalateFailed', 'Couldn’t send. Please try again.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white p-2.5 space-y-2">
      <p className="text-xs text-gray-600">{t('admin:help.chat.escalateIntro', 'The IAP team will see your question and this answer. Anything to add?')}</p>
      <textarea
        value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder={t('admin:help.chat.escalatePlaceholder', 'Optional — e.g. what you expected to happen')}
        className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="text-xs text-gray-500 hover:text-gray-800">{t('common:actions.cancel', 'Cancel')}</button>
        <button type="button" onClick={send} disabled={busy} className="rounded-md bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50">
          {busy ? t('common:actions.sendingEllipsis', 'Sending…') : t('admin:help.chat.escalateSend', 'Send to IAP support')}
        </button>
      </div>
    </div>
  );
}

function AnswerActions({ m }) {
  const { t } = useTranslation();
  const { rate, openArticle } = useHelpCentre();
  const [escalating, setEscalating] = useState(false);
  return (
    <div className="mt-1.5 space-y-1.5">
      {m.articles?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {m.articles.map((a) => (
            <button
              key={a.id} type="button" onClick={() => openArticle(a.id)}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:border-brand-400 hover:text-brand-700"
            >
              <BookOpenIcon className="h-3.5 w-3.5" />
              <span className="truncate max-w-[220px]">{a.title}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 text-gray-400">
        <span className="text-[11px]">{t('admin:help.chat.helpful', 'Helpful?')}</span>
        <button type="button" onClick={() => rate(m.questionId, 'up')} title={t('admin:help.chat.yes', 'Yes')} className="hover:text-green-600">
          {m.rating === 'up' ? <ThumbUpSolid className="h-4 w-4 text-green-600" /> : <HandThumbUpIcon className="h-4 w-4" />}
        </button>
        <button type="button" onClick={() => rate(m.questionId, 'down')} title={t('admin:help.chat.no', 'No')} className="hover:text-red-600">
          {m.rating === 'down' ? <ThumbDownSolid className="h-4 w-4 text-red-600" /> : <HandThumbDownIcon className="h-4 w-4" />}
        </button>
        {m.escalated ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-green-700"><CheckIcon className="h-3.5 w-3.5" />{t('admin:help.chat.escalated', 'Sent to IAP support')}</span>
        ) : !escalating && (
          <button type="button" onClick={() => setEscalating(true)} className="inline-flex items-center gap-1 text-[11px] hover:text-brand-700">
            <LifebuoyIcon className="h-3.5 w-3.5" />{t('admin:help.chat.escalateSend', 'Send to IAP support')}
          </button>
        )}
      </div>
      {escalating && !m.escalated && <EscalateBox questionId={m.questionId} onDone={() => setEscalating(false)} />}
    </div>
  );
}

const STARTERS_EN = [
  'How do I move a lead to Onboarding if they signed their contract outside the portal?',
  'How do I add my contract as a template?',
  'How do I record a part-payment on an invoice?',
];

export default function HelpChat({ onNavigate }) {
  const { t } = useTranslation();
  const { messages, sending, error, ask, resetChat } = useHelpCentre();
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  const submit = async (e) => {
    e?.preventDefault();
    const q = input;
    if (!q.trim()) return;
    setInput('');
    const ok = await ask(q);
    if (!ok) setInput(q);
  };

  const starters = [
    t('admin:help.chat.starter1', STARTERS_EN[0]),
    t('admin:help.chat.starter2', STARTERS_EN[1]),
    t('admin:help.chat.starter3', STARTERS_EN[2]),
  ];

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-0">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">{t('admin:help.chat.intro', 'Ask how to do anything in the portal. Answers come from the IAP guides.')}</p>
            <div className="space-y-1.5">
              {starters.map((s) => (
                <button
                  key={s} type="button" onClick={() => ask(s)} disabled={sending}
                  className="block w-full text-left rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 hover:border-brand-400 hover:bg-brand-50 disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (m.role === 'user' ? (
          <div key={i} className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl bg-brand-600 px-3.5 py-2 text-sm text-white whitespace-pre-wrap break-words">{m.text}</div>
          </div>
        ) : (
          <div key={i} className="flex items-start gap-2">
            <div className="h-6 w-6 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <SparklesIcon className="h-3.5 w-3.5 text-brand-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="rounded-2xl bg-gray-100 px-3.5 py-2.5">
                <HelpMarkdown text={m.text} onNavigate={onNavigate} />
              </div>
              <AnswerActions m={m} />
            </div>
          </div>
        )))}
        {sending && (
          <div className="flex items-start gap-2">
            <div className="h-6 w-6 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
              <SparklesIcon className="h-3.5 w-3.5 text-brand-600 animate-pulse" />
            </div>
            <div className="rounded-2xl bg-gray-100 px-3.5 py-2 text-sm text-gray-400">{t('admin:help.chat.thinking', 'Looking through the guides…')}</div>
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={submit} className="pt-3 mt-2 border-t border-gray-100 space-y-1.5">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submit(e); }}
            rows={1}
            disabled={sending}
            placeholder={t('admin:help.chat.placeholder', 'Ask a question…')}
            className="flex-1 resize-none rounded-2xl border border-gray-300 px-3.5 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
            style={{ maxHeight: 120 }}
          />
          <button type="submit" disabled={!input.trim() || sending} className="h-9 w-9 rounded-full bg-brand-600 text-white flex items-center justify-center disabled:opacity-40 flex-shrink-0">
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>
        {messages.length > 0 && (
          <button type="button" onClick={resetChat} disabled={sending} className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700">
            <ArrowPathIcon className="h-3 w-3" />{t('admin:help.chat.newConversation', 'New conversation')}
          </button>
        )}
      </form>
    </div>
  );
}
